import { createContext, useContext, useState, useCallback } from 'react'

const AppContext = createContext(null)

export const useApp = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within AppProvider')
  }
  return context
}

export const AppProvider = ({ children }) => {
  // Toast notifications
  const [toasts, setToasts] = useState([])

  // Modal state
  const [modal, setModal] = useState(null)

  // Loading state
  const [globalLoading, setGlobalLoading] = useState(false)

  // Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Add toast notification
  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now()
    const toast = { id, message, type }
    
    setToasts(prev => [...prev, toast])

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }, [])

  // Remove toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Toast shortcuts
  const showSuccess = useCallback((message) => addToast(message, 'success'), [addToast])
  const showError = useCallback((message) => addToast(message, 'error', 8000), [addToast])
  const showWarning = useCallback((message) => addToast(message, 'warning'), [addToast])
  const showInfo = useCallback((message) => addToast(message, 'info'), [addToast])

  // Open modal
  const openModal = useCallback((component, props = {}) => {
    setModal({ component, props })
  }, [])

  // Close modal
  const closeModal = useCallback(() => {
    setModal(null)
  }, [])

  // Confirm dialog
  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      openModal('confirm', {
        ...options,
        onConfirm: () => {
          closeModal()
          resolve(true)
        },
        onCancel: () => {
          closeModal()
          resolve(false)
        }
      })
    })
  }, [openModal, closeModal])

  // Toggle sidebar
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev)
  }, [])

  const value = {
    // Toast
    toasts,
    addToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,

    // Modal
    modal,
    openModal,
    closeModal,
    confirm,

    // Loading
    globalLoading,
    setGlobalLoading,

    // Sidebar
    sidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar
  }

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}

export default AppContext
